// Mock data for PVN Calculator

// Countries with their VAT rates
export const COUNTRIES = [
  { 
    code: 'LV', 
    name: 'Latvija', 
    flag: '🇱🇻',
    rates: [21, 12, 5]
  },
  { 
    code: 'LT', 
    name: 'Lietuva', 
    flag: '🇱🇹',
    rates: [21, 9, 5]
  },
  { 
    code: 'EE', 
    name: 'Igaunija', 
    flag: '🇪🇪',
    rates: [24, 13, 9]
  }
];

export const DEFAULT_COUNTRY = 'LV';

export const getCountryRates = (countryCode) => {
  const country = COUNTRIES.find(c => c.code === countryCode);
  if (!country) return COUNTRIES[0].rates;
  return country.rates;
};

export const getPVNRates = (countryCode) => {
  const rates = getCountryRates(countryCode);
  return [
    { value: rates[0], label: `${rates[0]}%` },
    { value: rates[1], label: `${rates[1]}%` },
    { value: rates[2], label: `${rates[2]}%` },
    { value: 'custom', label: 'Cits' }
  ];
};

export const CALCULATION_MODES = [
  { value: 'add', label: 'Summa bez PVN', description: 'Pievienot PVN' },
  { value: 'extract', label: 'Summa ar PVN', description: 'Izvilkt PVN' }
];

export const DEFAULT_MODE = 'add';

// Euro denominations for visualization - using CSS styled bills and coins
export const EURO_BILLS = [
  { value: 500, label: '500', color: '#8B5CF6' },
  { value: 200, label: '200', color: '#EAB308' },
  { value: 100, label: '100', color: '#22C55E' },
  { value: 50, label: '50', color: '#F97316' },
  { value: 20, label: '20', color: '#3B82F6' },
  { value: 10, label: '10', color: '#EF4444' },
  { value: 5, label: '5', color: '#6B7280' }
];

export const EURO_COINS = [
  { value: 2, label: '2€', size: 'large', bicolor: true },
  { value: 1, label: '1€', size: 'large', bicolor: true },
  { value: 0.5, label: '50c', size: 'medium', gold: true },
  { value: 0.2, label: '20c', size: 'medium', gold: true },
  { value: 0.1, label: '10c', size: 'medium', gold: true },
  { value: 0.05, label: '5c', size: 'small', copper: true },
  { value: 0.02, label: '2c', size: 'small', copper: true },
  { value: 0.01, label: '1c', size: 'small', copper: true }
];

// Break down amount into bills and coins
export const getMoneyBreakdown = (amount) => {
  if (!amount || amount <= 0 || !isFinite(amount)) return { bills: [], coins: [] };
  
  // Limit maximum amount to prevent infinite loops
  const MAX_AMOUNT = 100000;
  let remaining = Math.min(Math.round(amount * 100) / 100, MAX_AMOUNT);
  
  const bills = [];
  const coins = [];
  
  // Bills - use count instead of pushing individual bills for large amounts
  for (const bill of EURO_BILLS) {
    const count = Math.floor(remaining / bill.value);
    if (count > 0) {
      // Limit to max 20 of each denomination for display
      const displayCount = Math.min(count, 20);
      for (let i = 0; i < displayCount; i++) {
        bills.push(bill);
      }
      remaining = Math.round((remaining - count * bill.value) * 100) / 100;
    }
  }
  
  // Coins
  for (const coin of EURO_COINS) {
    const count = Math.floor((remaining + 0.001) / coin.value);
    if (count > 0) {
      const displayCount = Math.min(count, 20);
      for (let i = 0; i < displayCount; i++) {
        coins.push(coin);
      }
      remaining = Math.round((remaining - count * coin.value) * 100) / 100;
    }
  }
  
  return { bills, coins };
};

// Round to 2 decimal places (standard rounding)
const roundTo2 = (num) => Math.round(num * 100) / 100;

// Helper functions for calculations with rounding awareness
export const calculatePVN = (amount, rate, mode) => {
  const numAmount = parseFloat(amount) || 0;
  const numRate = parseFloat(rate) || 0;
  
  if (mode === 'add') {
    // Adding PVN to amount (amount is without PVN)
    // PVN summa = Cena bez PVN * PVN %
    const pvnAmountExact = numAmount * (numRate / 100);
    const totalWithPVNExact = numAmount + pvnAmountExact;
    
    return {
      amountWithoutPVN: numAmount,
      amountWithoutPVNExact: numAmount,
      pvnAmount: roundTo2(pvnAmountExact),
      pvnAmountExact: pvnAmountExact,
      amountWithPVN: roundTo2(totalWithPVNExact),
      amountWithPVNExact: totalWithPVNExact,
      hasRoundingIssue: false,
      mode: 'add'
    };
  } else {
    // Extracting PVN from amount (amount includes PVN)
    // PVN summa = Cena ar PVN — (Cena ar PVN / (1 + PVN %))
    const amountWithoutPVNExact = numAmount / (1 + numRate / 100);
    const pvnAmountExact = numAmount - amountWithoutPVNExact;
    
    // Rounded values
    const amountWithoutPVNRounded = roundTo2(amountWithoutPVNExact);
    const pvnAmountRounded = roundTo2(pvnAmountExact);
    
    // Check for rounding issue: recalculate from rounded bezPVN
    const pvnFromRounded = amountWithoutPVNRounded * (numRate / 100);
    const pvnFromRoundedRounded = roundTo2(pvnFromRounded);
    const totalFromRounded = roundTo2(amountWithoutPVNRounded + pvnFromRoundedRounded);
    
    // Detect if there's a rounding discrepancy
    const hasRoundingIssue = totalFromRounded !== numAmount;
    
    return {
      amountWithoutPVN: amountWithoutPVNRounded,
      amountWithoutPVNExact: amountWithoutPVNExact,
      pvnAmount: pvnAmountRounded,
      pvnAmountExact: pvnAmountExact,
      amountWithPVN: numAmount,
      amountWithPVNExact: numAmount,
      hasRoundingIssue: hasRoundingIssue,
      mode: 'extract',
      // If recalculated from rounded bezPVN value
      recalculated: hasRoundingIssue ? {
        amountWithoutPVN: amountWithoutPVNRounded,
        pvnAmount: pvnFromRoundedRounded,
        amountWithPVN: totalFromRounded
      } : null
    };
  }
};

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('lv-LV', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export const formatExact = (value, decimals = 8) => {
  return new Intl.NumberFormat('lv-LV', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: decimals
  }).format(value);
};
